import axios from 'axios';
import { refreshTokensSingleflight } from '../../api/auth-client/refresh-singleton';
import { sendGlobalMessage } from '../../containers/global-message/global-message.helpers';
import { clearAuthenticationStore, getRefreshToken, getRequestToken } from '../storage/storage';

/**
 * One-shot guard that prevents N parallel 401 callers (each with their own
 * AxiosInstance per `*-client.ts` module) from each independently calling
 * `clearAuthenticationStore()` + setting `window.location.href`. The first
 * caller wins; subsequent rejections still propagate via `Promise.reject(error)`
 * so React Query / caller `.catch` handlers see the failure, but no extra
 * Sentry/toast/heap teardown work happens. Module-level state is destroyed by
 * the navigation itself, so this flag is effectively per-session — there is
 * no need to reset it.
 */
let hasRedirectedToLogin = false;

/**
 * Centralizes the "session is unrecoverable, kick the user back to login"
 * teardown. Idempotent — guarded by `hasRedirectedToLogin` so 5 parallel 401s
 * sharing one rejected refresh do not each fan out 5× clear+redirect calls.
 *
 * @returns {void}
 */
function redirectToLoginOnce(): void {
  if (hasRedirectedToLogin) return;
  hasRedirectedToLogin = true;
  clearAuthenticationStore();
  window.location.href = '/?token=expired';
}

/**
 * Test-only: reset the one-shot redirect flag so test order doesn't matter.
 * Gated behind `import.meta.env.DEV` so it's `undefined` in production
 * bundles. NOT for production use.
 *
 * @returns {void}
 */
export const __resetRedirectGuardForTests: (() => void) | undefined = import.meta.env.DEV
  ? () => {
      hasRedirectedToLogin = false;
    }
  : undefined;

const AxiosInstance = (options?: Object) => {
  const axiosInstanceObject = axios.create(
    Object.assign(
      {},
      {
        timeout: 360000,
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        withCredentials: true,
      },
      options || {},
    ),
  );

  axiosInstanceObject.interceptors.request.use(function (config) {
    const access = getRequestToken();
    if (access !== undefined && config.headers != undefined) {
      config.headers.Authorization = `JWT ${getRequestToken()}`;
    }
    return config;
  });

  axiosInstanceObject.interceptors.response.use(
    (response) => {
      const hasGlobalMessages =
        response?.data?.global_messages &&
        Array.isArray(response.data.global_messages) &&
        response.data.global_messages.length > 0;
      if (hasGlobalMessages) {
        const defaultMessage = response.data.global_messages[0];
        sendGlobalMessage({
          message: defaultMessage.message || 'An error occurred',
        });
      }
      return response;
    },
    async function (error) {
      const originalRequest = error.config;

      // CASE 1: No error specified Most likely to be server error
      if (typeof error.response === 'undefined') {
        return Promise.reject(error);
      }

      // CASE 2: Refresh token is expired. User must login again
      if (error.response.status === 401 && originalRequest.url.includes('/auth/jwt/refresh/')) {
        redirectToLoginOnce();
        return Promise.reject(error);
      }

      // CASE 3: Access token is expired. We should try to refresh it.
      // Uses a single-flight singleton so parallel 401s from N concurrent
      // requests collapse into ONE refresh POST — required because the
      // backend blacklists refresh tokens after rotation, so a parallel
      // refresh storm would 401 all but one caller and force-log-out the user.
      if (
        !originalRequest._retry &&
        !originalRequest.url.includes('/auth/') &&
        (error.response.status === 401 ||
          (error.response.status === 403 &&
            'code' in error.response.data &&
            error.response.data.code === 'token_not_valid'))
      ) {
        originalRequest._retry = true;
        const refreshTokenValue = getRefreshToken();
        if (!refreshTokenValue) {
          redirectToLoginOnce();
          return Promise.reject(error);
        }
        try {
          await refreshTokensSingleflight(refreshTokenValue);
          // The singleton has already persisted the rotated tokens in cookies
          // AND verified they actually wrote (defense against silent js-cookie
          // failures on Safari ITP / quota), so the request interceptor will
          // pick up the fresh access token when the original request is retried.
          return axiosInstanceObject(originalRequest);
        } catch (_refreshErr) {
          // Refresh failed (genuinely expired, blacklisted, cookie-write
          // failure, or any other reason). Cannot recover this session —
          // redirect once and propagate. The original 401 is what callers
          // see in their .catch; the underlying refresh failure is logged
          // by axios + browser network panel for debugging.
          redirectToLoginOnce();
          return Promise.reject(error);
        }
      }
      return Promise.reject(error);
    },
  );

  return axiosInstanceObject;
};

export default AxiosInstance;
